import { Box, Divider, IconButton, Typography } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import { Avatar } from "../../../../components/fieldsViews/fields/Avatar";
import { Link } from "react-router-dom";
import { formatDateWithTime } from "../../../../utils/dateUtils";
import { WysiwygField } from "../../../../components/fieldsViews/fields/WysiwygField";
import { useNoteCategoryChoices } from "../../case-management-notes/useNoteCategoryChoices";
import { getChoiceLabel } from "../../../../utils/choiceUtils";
import { useNoteTitleChoices } from "../../case-management-notes/useNoteTitleChoices";
import { ColoredLabelChip } from "../../../../components/chips/ColoredLabelChip";
import { useRecord } from "../../../../api/useRecord";
import type { LogbookNoteItem } from "../../types";

const NOTE_FIELDS = "id,content,date_created,category,title,sessions,user_created.first_name,user_created.last_name,user_created.avatar";

type NoteDetailPanelProps = {
	note: LogbookNoteItem;
	treatmentId: string | number;
};

export const NoteDetailPanel = ({ note, treatmentId }: NoteDetailPanelProps) => {
	const initialData = { item: note, id: note.id, entity: "notes" };
	const { data: noteData } = useRecord("notes", NOTE_FIELDS, note.id, { initialData });
	const fullNote = noteData?.item ?? note;

	const categoryChoices = useNoteCategoryChoices();
	const titleChoices = useNoteTitleChoices();
	const categoryLabel = getChoiceLabel(categoryChoices, fullNote.category ?? "customer_support");
	const titleChoice = titleChoices.find((choice) => choice.value === fullNote.title);
	const dateLabel = formatDateWithTime(fullNote.date_created);
	const notePath = fullNote.category === "psychology" ? "psychology" : "notes";
	const noteUrl = `/treatments/${treatmentId}/${notePath}?[noteId]=${note.id}`;

	return (
		<Box>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="subtitle1" fontWeight="bold" color="primary">
					Nota
				</Typography>
				<IconButton
					color="secondary"
					component={Link}
					to={noteUrl}
					target="_blank"
					size="small"
				>
					<OpenInNew />
				</IconButton>
			</Box>

			<Box sx={{px:2, mx:2, mt: 2}}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2}}>
				{fullNote.user_created && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Avatar value={fullNote.user_created.avatar} styles={{ width: 25, height: 25 }} />
						<Typography variant="body2">
							{fullNote.user_created.first_name} {fullNote.user_created.last_name}
						</Typography>
					</Box>
				)}
				<Typography variant="body2" color="text.secondary">
					{dateLabel}
				</Typography>
				<ColoredLabelChip label={categoryLabel} color="#78909c" />
				{titleChoice?.color && (
					<ColoredLabelChip label={titleChoice.text} color={titleChoice.color} />
				)}
			</Box>
			<Divider sx={{ my:4 }} />
			<Box sx={{ '& *': { fontFamily: 'inherit !important' } }}>
				<WysiwygField
					key={`note-${note.id}`}
					field={{ field: "content", settings: {} } as any}
					value={fullNote.content || ""}
					label=""
					fieldView="group"
				/>
			</Box>
			</Box>
		</Box>
	);
};
